import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { storeAPI, inventoryAPI } from '../services/api';
import { 
  ShoppingBag, 
  ShoppingCart, 
  Star, 
  Clock, 
  CheckCircle,
  AlertCircle,
  Package,
  Gift,
  Heart
} from 'lucide-react';
import { toast } from 'react-toastify';

const Store = () => {
  const { user, updateUser } = useAuth();
  const [items, setItems] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(null);
  const [activeTab, setActiveTab] = useState('store'); // store, inventory

  useEffect(() => {
    fetchStoreData();
  }, []);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Smooth scroll to top when switching tabs
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  const fetchStoreData = async () => {
    try {
      setLoading(true);
      const [storeResponse, inventoryResponse] = await Promise.all([
        storeAPI.getItems(),
        inventoryAPI.getInventory(),
      ]);

      if (storeResponse.data.success) {
        setItems(storeResponse.data.items);
      }

      if (inventoryResponse.data.success) {
        setInventory(inventoryResponse.data.inventory);
      }
    } catch (error) {
      toast.error('Error loading store data! 😢');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (item) => {
    if (user.pookiePoints < item.pookiePointsCost) {
      toast.error(`You need ${item.pookiePointsCost} Pookie Points but only have ${user.pookiePoints}! 💔`);
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to purchase "${item.name}" for ${item.pookiePointsCost} Pookie Points? It will be added to your inventory! 🛒`);
    if (!confirmed) return;

    try {
      setPurchaseLoading(item._id);
      const response = await inventoryAPI.purchaseItem({ 
        itemId: item._id,
        specialMessage: '' 
      });

      if (response.data.success) {
        // Update user's points
        updateUser({
          ...user,
          pookiePoints: response.data.remainingPoints
        });

        // Add to inventory
        setInventory([response.data.inventoryItem, ...inventory]);
        
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error purchasing item! 😢');
    } finally {
      setPurchaseLoading(null);
    }
  };

  const handleUseItem = async (inventoryItem) => {
    const notes = prompt('Add any notes about using this item (optional):');
    try {
      const response = await inventoryAPI.useItem(inventoryItem._id, notes);
      if (response.data.success) {
        setInventory(inventory.map(item => 
          item._id === inventoryItem._id ? response.data.inventoryItem : item
        ));
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error using item! 😢');
    }
  };

  const StoreItem = ({ item }) => (
    <div className="card-cute group">
      {/* Mobile-optimized item header */}
      <div className="flex items-start space-x-3 mb-3">
        <span className="text-2xl xs:text-3xl flex-shrink-0">{item.emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-auto-scale-mobile flex-1">{item.name}</h3>
            {item.isSpecial && (
              <div className="bg-yellow-100 text-yellow-600 px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ml-2">
                ⭐ Special
              </div>
            )}
          </div>
          <p className="text-mobile-sm text-gray-600 text-full-description mb-2">{item.description}</p>
        </div>
      </div>

      {/* Mobile-optimized points and action section */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col xs:flex-row xs:items-center xs:space-x-2">
          <div className="points-display text-xs mb-1 xs:mb-0">
            💎 {item.pookiePointsCost}
          </div>
          {item.stock > 0 && (
            <span className="text-xs text-gray-500">
              {item.stock} left
            </span>
          )}
        </div>

        <button
          onClick={() => handlePurchase(item)}
          disabled={purchaseLoading === item._id || user.pookiePoints < item.pookiePointsCost}
          className={`btn-primary text-xs px-4 py-2 flex items-center space-x-1 ${
            user.pookiePoints < item.pookiePointsCost 
              ? 'opacity-50 cursor-not-allowed' 
              : 'active:scale-95'
          }`}
        >
          {purchaseLoading === item._id ? (
            <>
              <div className="loading-spinner w-3 h-3"></div>
              <span>Buying...</span>
            </>
          ) : (
            <>
              <ShoppingCart className="w-3 h-3" />
              <span>Buy</span>
            </>
          )}
        </button>
      </div>

      {/* Special message section */}
      {item.specialMessage && (
        <div className="mt-3 p-3 bg-pookie-pink-50 rounded-lg">
          <p className="text-xs text-pookie-pink-700">{item.specialMessage}</p>
        </div>
      )}
    </div>
  );

  const InventoryItem = ({ item }) => (
    <div className="card-cute">
      {/* Mobile-optimized inventory item header */}
      <div className="flex items-start space-x-3 mb-3">
        <span className="text-2xl xs:text-3xl flex-shrink-0">{item.itemEmoji}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-auto-scale-mobile flex-1">{item.itemName}</h3>
            <div className={`text-xs px-2 py-1 rounded-full ${
              item.isUsed 
                ? 'bg-gray-100 text-gray-600' 
                : 'bg-green-100 text-green-600'
            }`}>
              {item.isUsed ? '✅ Used' : '🎁 Available'}
            </div>
          </div>
          <p className="text-mobile-sm text-gray-600 text-full-description mb-2">{item.itemDescription}</p>
          
          {/* Mobile-optimized points display */}
          <div className="points-display text-xs mb-2">
            💎 {item.pookiePointsSpent}
          </div>
        </div>
      </div>

      {/* Special message and notes */}
      {item.specialMessage && (
        <div className="mb-3 p-3 bg-pookie-purple-50 rounded-lg">
          <p className="text-xs text-pookie-purple-700">{item.specialMessage}</p>
        </div>
      )}

      {item.usageNotes && (
        <div className="mb-3 p-3 bg-green-50 rounded-lg">
          <p className="text-xs text-green-700">
            <strong>Notes:</strong> {item.usageNotes}
          </p>
        </div>
      )}

      {/* Mobile-optimized footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <span>📅 {new Date(item.purchasedAt).toLocaleDateString()}</span>
          {item.isUsed && item.usedAt && (
            <span>• Used {new Date(item.usedAt).toLocaleDateString()}</span>
          )}
        </div>
        
        {!item.isUsed && (
          <button
            onClick={() => handleUseItem(item)}
            className="btn-success text-xs px-3 py-1 flex items-center space-x-1"
          >
            <CheckCircle className="w-3 h-3" />
            <span>Use</span>
          </button>
        )}
      </div>
    </div>
  );

  const availableInventory = inventory.filter(item => !item.isUsed);
  const usedInventory = inventory.filter(item => item.isUsed);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="loading-spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-mobile-y pb-20 lg:pb-0">
      {/* Header */}
      <div className="text-center px-mobile">
        <h1 className="text-mobile-3xl font-bold text-gradient text-cute mb-2">Pookie Store & Inventory</h1>
        <p className="text-mobile-base text-gray-600">Shop for rewards and manage your inventory! 🛒</p>
        <div className="mt-4">
          <div className="points-display text-sm mx-auto">
            💎 {user?.pookiePoints || 0} Pookie Points
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-container mx-4">
        {[
          { key: 'store', label: 'Store', icon: ShoppingBag },
          { key: 'inventory', label: 'My Inventory', icon: Package },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`tab-button ${activeTab === key ? 'active' : ''}`}
          >
            <Icon className="w-4 h-4" />
            <span className="text-mobile-sm">{label}</span>
          </button>
        ))}
      </div>

      {/* Store Tab */}
      {activeTab === 'store' && (
        <div className="space-mobile-y px-4">
          {/* Store Items - Mobile-optimized single column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item) => (
              <StoreItem key={item._id} item={item} />
            ))}
          </div>

          {items.length === 0 && (
            <div className="text-center py-12">
              <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-mobile-xl font-semibold text-gray-600 mb-2">No items found</h3>
              <p className="text-mobile-base text-gray-500">Check back later for new items! 💕</p>
            </div>
          )}
        </div>
      )}

      {/* Inventory Tab */}
      {activeTab === 'inventory' && (
        <div className="space-mobile-y px-4">
          {/* Available Items - Mobile-optimized single column grid */}
          {availableInventory.length > 0 && (
            <div>
              <h2 className="text-mobile-xl font-semibold mb-4 flex items-center">
                <Package className="w-5 h-5 mr-2" />
                Available Items ({availableInventory.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {availableInventory.map((item) => (
                  <InventoryItem key={item._id} item={item} />
                ))}
              </div>
            </div>
          )}

          {/* Used Items - Mobile-optimized single column grid */}
          {usedInventory.length > 0 && (
            <div>
              <h2 className="text-mobile-xl font-semibold mb-4 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                Used Items ({usedInventory.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {usedInventory.map((item) => (
                  <InventoryItem key={item._id} item={item} />
                ))}
              </div>
            </div>
          )}

          {inventory.length === 0 && (
            <div className="text-center py-12">
              <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-mobile-xl font-semibold text-gray-600 mb-2">No items in inventory</h3>
              <p className="text-mobile-base text-gray-500">Purchase items from the store to build your inventory! 💕</p>
            </div>
          )}
        </div>
      )}

      {/* Motivational Message */}
      <div className="text-center p-mobile bg-gradient-to-r from-pookie-pink-100 to-pookie-purple-100 rounded-2xl mx-4">
        <ShoppingBag className="w-8 h-8 mx-auto mb-2 text-pookie-pink-500" />
        <p className="text-mobile-lg font-semibold text-gray-800">
          Every Pookie Point you spend brings you closer to happiness! 💕
        </p>
      </div>
    </div>
  );
};

export default Store; 